<template>
  <article class="checkpoint-result-card" aria-label="Checkpoint result">
    <div class="checkpoint-result-card__body">
      <div class="checkpoint-result-card__type-row">
        <span class="checkpoint-result-card__chip">
          <svg
            class="checkpoint-result-card__chip-icon"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Result
        </span>
        <span class="checkpoint-result-card__step">{{ stepLabel }}</span>
      </div>

      <h3 class="checkpoint-result-card__title">You completed this checkpoint</h3>
      <p class="checkpoint-result-card__text">{{ explanation }}</p>

      <section class="checkpoint-result-card__meaning" aria-labelledby="checkpoint-result-meaning">
        <h4 id="checkpoint-result-meaning" class="checkpoint-result-card__meaning-title">
          What this means
        </h4>
        <ul class="checkpoint-result-card__meaning-list">
          <li>The checkpoint is marked complete on your record</li>
          <li>It counts toward your {{ level }} badge progress</li>
          <li>Your quiz result is saved with the lesson version you took</li>
        </ul>
      </section>
    </div>

    <footer class="checkpoint-result-card__footer">
      <span class="checkpoint-result-card__verdict">Checkpoint passed</span>
      <span class="checkpoint-result-card__count">
        {{ questionCount }} correctly answered out of {{ questionCount }}
      </span>
    </footer>
  </article>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  questionCount: { type: Number, default: 0 },
  level: { type: String, default: "Apprentice" },
  stepLabel: { type: String, default: "Step 3 of 3" },
});

const explanation = computed(
  () =>
    `You answered all ${props.questionCount} questions correctly. This checkpoint only requires a quiz, so no inspection is needed and it is now complete.`
);
</script>

<style scoped>
.checkpoint-result-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #eaecf0;
  border-left: 4px solid #079455;
  border-radius: 8px;
  background: #ffffff;
  box-sizing: border-box;
  color: #475467;
  font-family: Inter, sans-serif;
}

.checkpoint-result-card__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 16px 20px;
}

.checkpoint-result-card__type-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkpoint-result-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 8px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
}

.checkpoint-result-card__chip-icon {
  color: #079455;
}

.checkpoint-result-card__step {
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
}

.checkpoint-result-card__title {
  margin: 0;
  color: #101828;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
}

.checkpoint-result-card__text {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.checkpoint-result-card__meaning {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border: 1px solid #abefc6;
  border-radius: 8px;
  background: #ecfdf3;
}

.checkpoint-result-card__meaning-title {
  margin: 0;
  color: #067647;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.checkpoint-result-card__meaning-list {
  margin: 0;
  padding-left: 20px;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

.checkpoint-result-card__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 12px 20px;
  border-top: 1px solid #eaecf0;
  background: #f9fafb;
  font-size: 14px;
  line-height: 20px;
}

.checkpoint-result-card__verdict {
  color: #067647;
  font-weight: 600;
}

.checkpoint-result-card__count {
  color: #475467;
  font-weight: 400;
}
</style>
